/**
 * Challenge card header – layout, avatar, badges, and remind/cancel row.
 * Countdown/coin styles live in challengeCardHeaderCountdown.styles.ts.
 * Composed by challengeCardHeader.styles.ts.
 */

import { Platform } from 'react-native';
import { makeBoxShadow } from '../../shared/helpers/shadowStyles.helper';
import type { Theme } from '../../theme/getTheme';
import { getChallengeCardHeaderCountdownStyles } from './challengeCardHeaderCountdown.styles';

export function getChallengeCardHeaderLayoutStyles(theme: Theme, narrow: boolean) {
  return {
    homeHeaderRow: {
      flexDirection: 'column' as const,
      marginBottom: 0,
    },
    homeHeaderTopRow: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
      gap: 8,
    },
    homeHeaderTopLeft: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      flex: 1,
      minWidth: 0,
    },
    homeHeaderTopRight: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 6,
      flexShrink: 0,
    },
    homeActionsBlock: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 6,
    },
    homeChevronPill: {
      width: narrow ? 26 : 32,
      height: narrow ? 26 : 32,
      borderRadius: narrow ? 13 : 16,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      backgroundColor: theme.gray[300] + '70',
      ...(Platform.OS === 'web'
        ? makeBoxShadow({
            shadowColor: theme.gray[900],
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.06,
            shadowRadius: 2,
          })
        : {
            shadowColor: theme.gray[900],
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.06,
            shadowRadius: 2,
            elevation: 1,
          }),
    },
    homeChevronPillPressed: {
      opacity: 0.85,
    },
    homeHeaderAvatar: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: theme.background.card,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
      overflow: 'hidden',
    },
    homeHeaderCenter: {
      flex: 1,
      marginRight: 4,
      minWidth: 0,
    },
    homeHeaderName: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.black,
      marginBottom: 1,
      textAlign: 'left',
    },
    homeHeaderNameMedium: {
      fontSize: 13,
    },
    homeHeaderNameSmall: {
      fontSize: 12,
    },
    homeHeaderSubtitle: {
      fontSize: 11,
      color: theme.gray[700],
      textAlign: 'left',
    },
    homeHeaderSubtitleMedium: {
      fontSize: 10,
    },
    homeHeaderSubtitleSmall: {
      fontSize: 9,
    },
    homeHeaderPotRow: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 4,
      marginTop: 2,
    },
    homeHeaderPotLabel: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.gray[600],
      textTransform: 'uppercase' as const,
    },
    homeHeaderPotValue: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.accentDark,
    },
    homeHeaderFinishedAt: {
      fontSize: 11,
      color: theme.gray[500],
      marginRight: 4,
    },
    homeHeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    homeHeaderChevron: {
      marginLeft: 2,
    },
    homeExpandRow: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      marginTop: 0,
      paddingVertical: 0,
      minHeight: 0,
    },
    homeHeaderChevronWrap: {
      opacity: 0.7,
    },
    homeBadgeWithIcon: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      paddingHorizontal: 6,
      paddingVertical: 3,
      borderRadius: 10,
    },
    homeBadgeError: {
      backgroundColor: theme.error,
    },
    homeBadgeSuccess: {
      backgroundColor: theme.success,
    },
    homeBadgeWarning: {
      backgroundColor: theme.warning,
    },
    homeBadgeText: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.textOnStatusBadge,
    },
    homeRemindCancelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      marginTop: 2,
    },
    homeRemindCancelLink: {
      fontSize: 10,
      color: theme.primary,
      fontWeight: '600',
    },
    homeRemindCancelDivider: {
      fontSize: 10,
      color: theme.gray[400],
    },
    ...getChallengeCardHeaderCountdownStyles(theme, narrow),
  };
}
